import React, { useRef, useState, useEffect, useMemo } from "react";
import {
  Card,
  Button,
  ProgressBar,
  Spinner
} from "react-bootstrap";
import api from '../../api/api'
import { useTranslation } from "react-i18next";

import img from "../../assets/images/avatars/default-avatar.png"

function UserDetails() {
  const [data, setData] = useState({ name: "", email: "", address: "", upload_file_name: "", description: "" });
  const [selectedFile, setSelectedFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const [msgServer, setMsgServer] = useState("");
  const [classMsg, setClassMsg] = useState("");
  const [displayMsg, setDisplayMsg] = useState("hide");
  const [iconMsg, setIconMsg] = useState("info");
  
  const { t } = useTranslation();

  const fileRef = useRef();
  const cardRef = useRef();
  const imgRef = useRef(null);
  const lastUpdatedDataRef = useRef({});
  const isInitialMount = useRef(true);

  // Fonction pour émettre l'événement de mise à jour de l'avatar
  const notifyAvatarUpdated = (newAvatarUrl) => {
    // Créer un événement personnalisé avec les données de l'avatar
    const event = new CustomEvent('avatar-updated', { 
      detail: { 
        avatarUrl: newAvatarUrl 
      } 
    });
    window.dispatchEvent(event);
    
    // Émettre aussi l'événement générique de mise à jour utilisateur
    window.dispatchEvent(new Event("user-updated"));
  };

  // Fonction pour récupérer les données du profil depuis l'API
  const fetchProfileData = async () => {
    try {
      const response = await api.get('/profil');
      const newData = response.data;
      setData(newData.result);
      
      // Stocker les dernières données récupérées pour comparaison future
      lastUpdatedDataRef.current = {
        name: newData.result.name,
        description: newData.result.description,
        upload_file_name: newData.result.upload_file_name
      };
      
      return newData.result;
    } catch (error) {
      console.error("Erreur lors du chargement du profil:", error);
    }
  };

  // Gestion du changement de fichier
  const handleChange = async (e) => {
    const [file] = e.target.files;
    setSelectedFile(file);
  };
  
  // Soumission du fichier d'avatar
  const handleSubmitFile = async (e) => {
    setIsLoading(true); // Activer le loader uniquement pour l'upload
    
    const formData = new FormData();
    formData.append(
      "userPhoto",
      selectedFile,
      selectedFile.name
    );
    
    let $error = false;
    const maxSize = 1 * 1024 * 1024; // 1MB max
    
    if (selectedFile.size > maxSize) {
      $error = true;
      setMsgServer("avatar_max_size_error");
      setClassMsg("danger");
      setDisplayMsg("show");
      setIconMsg("info");
      setIsLoading(false);
      return false;
    }
    
    if ((selectedFile.type === "image/png" || selectedFile.type === "image/jpg" || selectedFile.type === "image/jpeg") && $error === false) {
      try {
        const response = await api.post('/upload-avatar', formData);
        
        if (response.data.type && response.data.type === "success") {
          let user = JSON.parse(localStorage.getItem("user") || "{}");
          const newAvatarName = `userPhoto-${user.id}.jpeg`;
          user.avatar = newAvatarName;
          localStorage.setItem("user", JSON.stringify(user));

          setMsgServer(response.data.message);
          setClassMsg("success");
          setDisplayMsg("show");
          setIconMsg("check");
          
          // Réinitialiser le sélecteur de fichier
          setSelectedFile(null);
          
          // Mettre à jour directement l'état local avec la nouvelle image
          setData(prevData => ({
            ...prevData,
            upload_file_name: newAvatarName
          }));
          
          // Déclencher une invalidation du cache d'image en ajoutant un timestamp
          if (imgRef.current) {
            const timestamp = new Date().getTime();
            const currentSrc = imgRef.current.src;
            if (currentSrc.includes('?')) {
              imgRef.current.src = currentSrc.split('?')[0] + '?' + timestamp;
            } else {
              imgRef.current.src = currentSrc + '?' + timestamp;
            }
          }
          
          // Notifier les autres composants de la mise à jour de l'avatar
          notifyAvatarUpdated(newAvatarName);
          
          // Animation discrète de mise à jour
          triggerSilentUpdate();
          
          // Masquer le loader
          setIsLoading(false);
        }
        
        if (response.data.type && response.data.type === "danger") {
          setMsgServer(response.data.message);
          setClassMsg("danger");
          setDisplayMsg("show");
          setIconMsg("info");
          setIsLoading(false);
        }
      } catch (error) {
        setMsgServer("avatar_upload_error");
        setClassMsg("danger");
        setDisplayMsg("show");
        setIconMsg("info");
        setIsLoading(false);
        return false;
      }
    } else {
      setMsgServer("avatar_format_error");
      setClassMsg("danger");
      setDisplayMsg("show");
      setIconMsg("info");
      setIsLoading(false);
      return false;
    }
  };

  // Déclencher une mise à jour discrète sans overlay visible
  const triggerSilentUpdate = () => {
    // Simplement mettre à jour le déclencheur pour activer l'animation
    setRefreshTrigger(prev => prev + 1);
  };

  // Écouter l'événement personnalisé de mise à jour de profil
  useEffect(() => {
    const handleProfileUpdate = () => {
      // Court délai pour permettre au localStorage d'être mis à jour
      setTimeout(async () => {
        // Charger les données à jour
        await fetchProfileData();
        // Déclencher l'animation discrète
        triggerSilentUpdate();
      }, 300);
    };

    // Ajouter l'écouteur d'événement
    window.addEventListener('profileUpdated', handleProfileUpdate);
    
    // Nettoyer l'écouteur lors du démontage
    return () => {
      window.removeEventListener('profileUpdated', handleProfileUpdate);
    };
  }, []);

  // Effet pour charger les données au montage initial
  useEffect(() => {
    fetchProfileData();
    
    // Vérifier une fois au démarrage si les données du localStorage diffèrent
    const checkInitialData = async () => {
      try {
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        if (user && (user.name || user.description || user.avatar)) {
          await fetchProfileData();
        }
      } catch (error) {
        console.error("Erreur lors de la vérification initiale:", error);
      }
    };
    
    checkInitialData();
  }, []);

  // Effet pour vérifier les changements quand le composant se monte
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // Vérifier si les données du localStorage sont différentes des données actuelles
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    if (user) {
      let needsUpdate = false;
      
      if (user.name && user.name !== data.name) {
        setData(prev => ({ ...prev, name: user.name }));
        needsUpdate = true;
      }
      
      if (user.description && user.description !== data.description) {
        setData(prev => ({ ...prev, description: user.description }));
        needsUpdate = true;
      }
      
      if (user.avatar && user.avatar !== data.upload_file_name) {
        setData(prev => ({ ...prev, upload_file_name: user.avatar }));
        needsUpdate = true;
      }
      
      if (needsUpdate) {
        triggerSilentUpdate();
      }
    }
  }, [refreshTrigger]);

  // Effet pour masquer le message après un certain temps
  useEffect(() => {
    if (displayMsg === "show") {
      const timer = setTimeout(() => {
        setDisplayMsg("hide");
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [displayMsg]);

  // Effet d'animation pour le card quand refreshTrigger change
  useEffect(() => {
    if (refreshTrigger > 0 && cardRef.current) {
      cardRef.current.classList.add('card-refresh-animation');
      setTimeout(() => {
        if (cardRef.current) {
          cardRef.current.classList.remove('card-refresh-animation');
        }
      }, 700);
    }
  }, [refreshTrigger]);

  // Générer un URL d'image avec timestamp pour éviter le cache
  // (le timestamp ne change que lorsque le fichier avatar change réellement,
  // pas à chaque rendu, pour éviter de recharger l'image inutilement)
  const avatarCacheBust = useMemo(() => Date.now(), [data.upload_file_name]);
  const getImageUrl = () => {
    if (!data.upload_file_name) {
      return img;
    }

    return `${process.env.REACT_APP_BASE_URL}/static/${data.upload_file_name}?t=${avatarCacheBust}`;
  };

  return (
    <>
      {/* Style pour l'animation de rafraîchissement */}
      <Card className="profile-summary-card" ref={cardRef} key={refreshTrigger}>
        <div className={`profile-inline-alert alert-${classMsg} fade ${displayMsg}`}>
          <i className={`fa fa-${iconMsg}`} aria-hidden="true" /> {t(msgServer)}
        </div>
        <Card.Header className="profile-summary-header">
          <div className={`profile-avatar-shell ${isLoading ? 'upload-loading' : ''}`}>
            <img
              ref={imgRef}
              className="profile-avatar-image"
              src={getImageUrl()}
              alt={data.name || t("profile_avatar", { defaultValue: "Photo de profil" })}
            />
            <button
              type="button"
              className="profile-avatar-edit"
              onClick={() => fileRef.current?.click()}
              disabled={isLoading}
              aria-label={t("avatar")}
            >
              <i className="fas fa-camera" aria-hidden="true" />
            </button>
          </div>
          <span className="profile-status-dot">
            <i aria-hidden="true" />
            {t("profile_active_account", { defaultValue: "Compte actif" })}
          </span>
          <h4>{data.name || "—"}</h4>
          <p className="profile-summary-email">{data.email || "—"}</p>

          <Button 
            type="button"
            className="profile-secondary-button"
            onClick={() => fileRef.current?.click()}
            disabled={isLoading}
          >
            <i className="far fa-image" aria-hidden="true" />
            {t("profile_choose_photo", { defaultValue: "Choisir une photo" })}
          </Button>
          
          {selectedFile && (
            <Button 
              type="button"
              className="profile-primary-button"
              onClick={handleSubmitFile}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Spinner 
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                  />
                  {t('uploading')}
                </>
              ) : (
                t('upload')
              )}
            </Button>
          )}

          <input
            ref={fileRef}
            onChange={handleChange}
            multiple={false}
            type="file"
            accept="image/png,image/jpeg"
            hidden
          />
        </Card.Header>
        <div className="profile-summary-content">
          <div className="profile-completion">
            <div className="progress-wrapper">
              <strong>
                {t("profile_completion", { defaultValue: "Profil complété" })}
              </strong>
              <div className="profile-progress-row">
                <ProgressBar now={100} className="progress-sm" />
                <span className="progress-value">
                  100%
                </span>
              </div>
            </div>
          </div>
          <div className="profile-description">
            <span className="profile-description-icon">
              <i className="far fa-file-alt" aria-hidden="true" />
            </span>
            <strong>
              {t('desc')}
            </strong>
            <p>{data.description || t("profile_no_description", { defaultValue: "Aucune description renseignée." })}</p>
          </div>
        </div>
      </Card>
    </>
  );
}

export default UserDetails;
